// src/components/CSVImportModal.jsx
import React, { useState, useRef } from 'react';
import { LeadIntake } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function CSVImportModal({ sources, onClose, onImported }) {
    const { user }      = useAuth();
    const [file,        setFile]      = useState(null);
    const [sourceId,    setSourceId]  = useState('');
    const [importing,   setImporting] = useState(false);
    const [result,      setResult]    = useState(null);
    const [err,         setErr]       = useState('');
    const fileRef = useRef();

    // Only show non-webhook sources (manual/csv makes most sense)
    const uploadSources = sources.filter(s => ['manual', 'custom', 'indiamart', 'tradeindia', 'justdial'].includes(s.slug));

    const handleImport = async () => {
        if (!file)     { setErr('Please select a CSV file'); return; }
        if (!sourceId) { setErr('Please select a source');  return; }

        setImporting(true); setErr(''); setResult(null);
        try {
            const res = await LeadIntake.importCSV(file, sourceId, user);
            if (res.error) { setErr(res.error); return; }
            setResult(res);
        } catch (ex) {
            setErr(ex.message);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div style={{
            position:'fixed', inset:0, background:'rgba(0,0,0,.6)',
            display:'flex', alignItems:'center', justifyContent:'center',
            zIndex:2000, padding:'1rem',
        }}>
            <div style={{
                background:'var(--card-bg,#fff)', borderRadius:16,
                width:'100%', maxWidth:480,
                boxShadow:'0 25px 60px rgba(0,0,0,.35)',
            }}>
                {/* Header */}
                <div style={{
                    padding:'1rem 1.25rem',
                    borderBottom:'1px solid var(--border,#e2e8f0)',
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                    background:'var(--bg,#f8fafc)', borderRadius:'16px 16px 0 0',
                }}>
                    <div style={{ fontWeight:700, fontSize:'1rem' }}>⬆ Import Leads from CSV</div>
                    <button onClick={onClose} style={{
                        background:'none', border:'none', fontSize:'1.3rem',
                        cursor:'pointer', color:'var(--muted)', lineHeight:1,
                    }}>✕</button>
                </div>

                <div style={{ padding:'1.2rem 1.25rem' }}>
                    {err && (
                        <div style={{
                            background:'#fee2e2', color:'#dc2626',
                            border:'1px solid #fca5a5', borderRadius:8,
                            padding:'8px 12px', fontSize:'.875rem', marginBottom:'1rem',
                        }}>{err}</div>
                    )}

                    {!result ? (
                        <>
                            {/* Source selector */}
                            <label style={{ marginBottom:'1rem' }}>
                                Lead Source
                                <select value={sourceId} onChange={e => setSourceId(e.target.value)}>
                                    <option value="">— Select source —</option>
                                    {uploadSources.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </label>

                            {/* File picker */}
                            <label style={{ marginBottom:'1rem' }}>
                                CSV File
                                <div
                                    onClick={() => fileRef.current.click()}
                                    style={{
                                        border:'2px dashed var(--border,#e2e8f0)',
                                        borderRadius:10, padding:'1.5rem',
                                        textAlign:'center', cursor:'pointer',
                                        background: file ? '#f0fdf4' : 'var(--bg,#f8fafc)',
                                        transition:'background .15s',
                                    }}>
                                    <div style={{ fontSize:'2rem', marginBottom:'.3rem' }}>
                                        {file ? '✅' : '📄'}
                                    </div>
                                    <div style={{ fontSize:'.875rem', color:'var(--text)' }}>
                                        {file ? file.name : 'Click to select CSV file'}
                                    </div>
                                    <div style={{ fontSize:'.72rem', color:'var(--muted)', marginTop:'.3rem' }}>
                                        .csv format only · Excel: Save As → CSV
                                    </div>
                                </div>
                                <input ref={fileRef} type="file" accept=".csv,.txt"
                                    style={{ display:'none' }}
                                    onChange={e => setFile(e.target.files[0])} />
                            </label>

                            {/* Column guide */}
                            <div style={{
                                background:'#eff6ff', border:'1px solid #bfdbfe',
                                borderRadius:8, padding:'.75rem', marginBottom:'1rem',
                            }}>
                                <div style={{ fontSize:'.72rem', fontWeight:700, color:'#1e40af',
                                    textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'.4rem' }}>
                                    📋 Accepted Column Headers (case-insensitive)
                                </div>
                                <div style={{ fontSize:'.75rem', color:'#1e40af', lineHeight:1.7 }}>
                                    <code>name</code> or <code>full_name</code> &nbsp;·&nbsp;
                                    <code>phone</code> or <code>mobile</code> &nbsp;·&nbsp;
                                    <code>email</code> &nbsp;·&nbsp;
                                    <code>company</code> &nbsp;·&nbsp;
                                    <code>city</code> &nbsp;·&nbsp;
                                    <code>product</code> or <code>requirement</code> &nbsp;·&nbsp;
                                    <code>message</code> or <code>notes</code>
                                </div>
                                <div style={{ fontSize:'.72rem', color:'#3b82f6', marginTop:'.4rem' }}>
                                    Duplicates (by phone) are skipped automati  y.
                                </div>
                            </div>
                        </>
                    ) : (
                        /* ── Import result ── */
                        <div style={{ textAlign:'center', padding:'1rem 0' }}>
                            <div style={{ fontSize:'3rem', marginBottom:'.5rem' }}>
                                {result.imported > 0 ? '✅' : '⚠️'}
                            </div>
                            <div style={{ fontWeight:700, fontSize:'1.1rem', marginBottom:'1rem' }}>
                                Import Complete
                            </div>
                            <div style={{ display:'flex', gap:'1rem', justifyContent:'center', marginBottom:'1rem' }}>
                                {[
                                    { label:'Imported', count:result.imported, color:'#166534', bg:'#dcfce7' },
                                    { label:'Skipped',  count:result.skipped,  color:'#92400e', bg:'#fef3c7' },
                                ].map(s => (
                                    <div key={s.label} style={{
                                        background:s.bg, color:s.color,
                                        border:`1px solid ${s.color}30`,
                                        borderRadius:10, padding:'8px 20px', minWidth:90,
                                    }}>
                                        <div style={{ fontSize:'1.5rem', fontWeight:800 }}>{s.count}</div>
                                        <div style={{ fontSize:'.72rem', fontWeight:600,
                                            textTransform:'uppercase', letterSpacing:'.05em' }}>{s.label}</div>
                                    </div>
                                ))}
                            </div>
                            {result.errors?.length > 0 && (
                                <div style={{ textAlign:'left', background:'#fef3c7', borderRadius:8,
                                    padding:'.75rem', fontSize:'.75rem', color:'#92400e', maxHeight:100, overflowY:'auto' }}>
                                    {result.errors.map((e, i) => <div key={i}>{e}</div>)}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding:'.9rem 1.25rem',
                    borderTop:'1px solid var(--border,#e2e8f0)',
                    display:'flex', gap:'.6rem', justifyContent:'flex-end',
                    background:'var(--bg,#f8fafc)', borderRadius:'0 0 16px 16px',
                }}>
                    {!result ? (
                        <>
                            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                            <button className="btn btn-primary"
                                onClick={handleImport}
                                disabled={importing || !file || !sourceId}>
                                {importing ? 'Importing…' : 'Import Leads'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="btn btn-ghost" onClick={() => setResult(null)}>
                                Import Another
                            </button>
                            <button className="btn btn-primary" onClick={onImported}>
                                Done
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
